package at.theatervogelweide.admin.contract.acts

import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.ImageDetails
import at.theatervogelweide.admin.contract.Validation
import at.theatervogelweide.admin.contract.VersionedId
import at.theatervogelweide.admin.contract.isImageUuid
import at.theatervogelweide.admin.contract.validate
import at.theatervogelweide.domain.TicketAnnouncement
import at.theatervogelweide.domain.TicketSchedule
import kotlinx.datetime.LocalDateTime
import kotlinx.serialization.Serializable

// All timestamps are in UTC, as stored in the database; the web app converts them from and to theater time.

/** A row of the list. */
@Serializable
data class ActListEntry(
    val id: Long,
    val version: Long,
    val title: String,
    val year: Int,
    val mainImageUuid: String?,
    /** Sorted ascending; the first is the premiere. */
    val performances: List<LocalDateTime>,
    val ticketSaleStart: LocalDateTime?,
) {
    val versionedId: VersionedId get() = VersionedId(id, version)

    val ticketSchedule: TicketSchedule? get() = TicketAnnouncement.schedule(ticketSaleStart, performances)
}

/**
 * The act the public ticket page shows at [now], like the public server's `TicketReservationServiceImpl`: of the acts
 * with a ticket sale and a performance still to come, the one whose next performance is the earliest, once it is
 * announced. `null` while that act isn't announced yet or there is none.
 */
fun List<ActListEntry>.onTicketPage(now: LocalDateTime): ActListEntry? {
    return nextOnTicketPage(now)?.takeIf { TicketAnnouncement.announcedFrom(it.ticketSaleStart!!) <= now }
}

/**
 * The act the public ticket page shows at [now] or, while it isn't announced yet, will show next: of the acts with a
 * ticket sale and a performance still to come, the one whose next performance is the earliest. Its
 * [ActListEntry.ticketSchedule] is never `null`.
 */
fun List<ActListEntry>.nextOnTicketPage(now: LocalDateTime): ActListEntry? {
    return TicketAnnouncement.nextOnTicketPage(this, now, { it.id }, { it.ticketSaleStart }, { it.performances })
}

/** A person who can be cast, as the token input offers and shows them. */
@Serializable
data class CastPerson(
    val id: Long,
    val firstName: String,
    val lastName: String,
) {
    val name: String get() = "$firstName $lastName".trim()
}

/**
 * A role and the people playing it, or a function such as "Regie" and the people doing it, in the order the public
 * site shows them. [id] is the role's id once saved, so saving keeps the roles it still lists, also when one moves to
 * the other group.
 */
@Serializable
data class CastRole(
    val id: Long? = null,
    val name: String = "",
    val personIds: List<Long> = emptyList(),
)

/**
 * A performance; [soldOut] shows it as sold out on the public site and disables its reservation link (#103). [id] is
 * the saved performance's, so saving keeps its row, also when its time changes: the public site's calendar knows it by
 * its id (#111).
 */
@Serializable
data class Performance(
    val startsAt: LocalDateTime,
    val soldOut: Boolean = false,
    val id: Long? = null,
)

/** A saved act, as the editor loads it. */
@Serializable
data class ActEditor(
    val id: Long,
    val version: Long,
    val draft: ActDraft,
    /** The people [ActDraft.cast] and [ActDraft.crew] name, so the editor shows them before the search has loaded everyone. */
    val castPeople: List<CastPerson>,
) {
    val versionedId: VersionedId get() = VersionedId(id, version)
}

/** What the editor creates and saves. */
@Serializable
data class ActDraft(
    val title: String = "",
    val year: Int = 0,
    /** Markdown, which the public site renders. */
    val description: String = "",
    /** Plain text for the start page and link previews; blank for none, then the public site uses the description. */
    val shortText: String = "",
    /** Distinct start times; the earliest is the premiere. */
    val performances: List<Performance> = emptyList(),
    /** From then on tickets can be reserved; the ticket page shows the act two weeks before. */
    val ticketSaleStart: LocalDateTime? = null,
    /** Blank for none. */
    val reservationUrl: String = "",
    /** Where the act is played; blank for none. A new act starts with the previous one's (ActService.newDraft). */
    val venueName: String = "",
    /** Plain text, one line per line of the address; blank for none. */
    val venueAddress: String = "",
    /** Markdown, e.g. the prices; blank for none. */
    val admission: String = "",
    /** "Besetzung": who is on stage. */
    val cast: List<CastRole> = emptyList(),
    /** "Hinter der Bühne": the crew. The public site names the role "Regie" here as the director. */
    val crew: List<CastRole> = emptyList(),
    /** The title image of the act page. */
    val mainImageUuid: String? = null,
    /** The other images, in order. */
    val imageUuids: List<String> = emptyList(),
    /**
     * The details of the title image and the other images (#105); one without an entry has none, and entries of other
     * images are ignored.
     */
    val imageDetails: Map<String, ImageDetails> = emptyMap(),
) {
    fun validate(): List<FieldError> = validate {
        field(TITLE, title) { required(); maxLength(255) }
        check(YEAR, year in MIN_YEAR..MAX_YEAR) { "Bitte ein Jahr zwischen $MIN_YEAR und $MAX_YEAR eingeben." }
        field(DESCRIPTION, description) { maxLength(10000) }
        check(SHORT_TEXT, shortText.trim().length <= SHORT_TEXT_LENGTH) {
            "Bitte höchstens $SHORT_TEXT_LENGTH Zeichen (jetzt ${shortText.trim().length})."
        }
        check(PERFORMANCES, performances.map { it.startsAt }.distinct().size == performances.size) {
            "Zwei Vorstellungen beginnen zur selben Zeit. Bitte eine davon entfernen."
        }
        // a saved performance listed twice would write both into its one row
        check(PERFORMANCES, performances.mapNotNull { it.id }.let { ids -> ids.distinct().size == ids.size }) {
            "Die Vorstellungen sind ungültig. Bitte neu laden."
        }
        check(TICKET_SALE_START, ticketSaleStart == null || performances.isEmpty() || ticketSaleStart < performances.maxOf { it.startsAt }) {
            "Der Kartenverkauf muss vor der letzten Vorstellung beginnen."
        }
        field(RESERVATION_URL, reservationUrl.trim()) { maxLength(2000) }
        check(RESERVATION_URL, reservationUrl.isBlank() || WEB_ADDRESS.matches(reservationUrl.trim())) {
            "Bitte eine Adresse beginnend mit https:// eingeben."
        }
        field(VENUE_NAME, venueName.trim()) { maxLength(255) }
        field(VENUE_ADDRESS, venueAddress.trim()) { maxLength(500) }
        field(ADMISSION, admission.trim()) { maxLength(2000) }
        cast.forEachIndexed { index, role -> validateRole(castField(index), role) }
        crew.forEachIndexed { index, role -> validateRole(crewField(index), role) }
        check(CAST, (cast + crew).mapNotNull { it.id }.let { ids -> ids.distinct().size == ids.size }) {
            "Die Besetzung ist ungültig. Bitte neu laden."
        }
        check(MAIN_IMAGE, mainImageUuid == null || isImageUuid(mainImageUuid)) {
            "Das Titelbild ist ungültig. Bitte neu laden."
        }
        check(
            IMAGES,
            imageUuids.all(::isImageUuid) && imageUuids.distinct().size == imageUuids.size && mainImageUuid !in imageUuids,
        ) { "Die Bilder sind ungültig. Bitte neu laden." }
        // the details of images the draft no longer has are left out, like when saving
        imageDetails.filterKeys { it == mainImageUuid || it in imageUuids }
            .forEach { (uuid, details) -> errors += details.validate(if (uuid == mainImageUuid) MAIN_IMAGE else IMAGES) }
    }

    private fun Validation.validateRole(field: String, role: CastRole) {
        field(field, role.name) { required(); maxLength(255) }
        // the current public site still draws a role of this name as a divider, hiding its people
        check(field, role.name.trim() != LEGACY_DIVIDER) { "„$LEGACY_DIVIDER“ ist als Name einer Rolle nicht möglich." }
        check(field, role.personIds.distinct().size == role.personIds.size) { "Eine Person steht zweimal in dieser Rolle." }
    }

    companion object {
        const val TITLE = "title"
        const val YEAR = "year"
        const val DESCRIPTION = "description"
        const val SHORT_TEXT = "shortText"
        const val PERFORMANCES = "performances"
        const val TICKET_SALE_START = "ticketSaleStart"
        const val RESERVATION_URL = "reservationUrl"
        const val VENUE_NAME = "venueName"
        const val VENUE_ADDRESS = "venueAddress"
        const val ADMISSION = "admission"
        const val CAST = "cast"
        const val CREW = "crew"
        const val MAIN_IMAGE = "mainImage"
        const val IMAGES = "images"

        const val MIN_YEAR = 1900
        const val MAX_YEAR = 2100
        const val SHORT_TEXT_LENGTH = 200

        /** How the cast's dividers were stored before #104; the current public site still draws such a role as one. */
        const val LEGACY_DIVIDER = "---"

        /** The field of the cast's role at [index], e.g. `cast.3`. */
        fun castField(index: Int) = "$CAST.$index"

        /** The field of the crew's role at [index], e.g. `crew.0`. */
        fun crewField(index: Int) = "$CREW.$index"

        private val WEB_ADDRESS = Regex("^https?://[^\\s/?#]+\\.[^\\s]*$", RegexOption.IGNORE_CASE)
    }
}
